import React, {useEffect, useState} from 'react';
import {useNavigate, useOutletContext} from 'react-router-dom';
import {getJobPostingListForMyTask} from '../../api/jobApi';
import {JobPostingData} from '../../interface/JobPostingData';
import {EmptyState} from "../../components/common/EmptyStateProps";

// 비용(조정 금액 우선) / 최초 비용(공고 원가) 계산
const getCostInfo = (job: JobPostingData) => {
    const original = Number(job.dailyPay) || 0;
    const matched = (job.additionalPay !== undefined && job.additionalPay !== null && job.additionalPay !== '')
        ? Number(job.additionalPay) : null;
    const cost = matched !== null ? matched : original;
    const showOriginal = matched !== null && matched !== original;
    return { cost, original, showOriginal };
};

const getStatusDisplay = (job: JobPostingData) => {
    // 다른 코디가 매칭되어 내가 탈락한 경우 (백엔드 계산값)
    if (job.matchFailed) {
        return { label: '매칭 실패', className: 'status-cancel' };
    }
    switch (job.status) {
        case 'RECRUITING': return { label: '매칭 대기', className: 'status-waiting' };
        case 'MATCHED':    return { label: '매칭 완료', className: 'status-matched' };
        case 'ONGOING':    return { label: '진행 중',   className: 'status-ongoing' };
        case 'COMPLETED':  return { label: '완료',      className: 'status-completed' };
        case 'CANCELLED':  return { label: '취소',      className: 'status-cancel' };
        default:           return { label: job.status ?? '', className: '' };
    }
};

// 공고가 어느 탭에 속하는지 결정. 매칭 실패 건은 상태와 무관하게 '완료' 탭.
const getJobTab = (job: JobPostingData): string => {
    if (job.matchFailed) return 'completed';
    switch (job.status) {
        case 'RECRUITING':
        case 'MATCHED':    return 'matching';
        case 'ONGOING':    return 'ongoing';
        case 'COMPLETED':
        case 'CANCELLED':  return 'completed';
        default:           return '';
    }
};

interface OutletContextType {
    workTypeFilter: string;
    setLoading: (loading: boolean) => void;
    activeTab: string;
}

export default function MyTaskPage() {
    const navigate = useNavigate();
    const {workTypeFilter, setLoading, activeTab} = useOutletContext<OutletContextType>();
    const [jobList, setJobList] = useState<JobPostingData[]>([]);

    useEffect(() => {
        fetchJobList();
    }, [workTypeFilter, activeTab]);

    const fetchJobList = async () => {
        try {
            const workType = workTypeFilter === 'ALL' ? '' : workTypeFilter;
            const response = await getJobPostingListForMyTask({ workType });
            const allJobs = response.data ?? [];

            setJobList(allJobs.filter(job => getJobTab(job) === activeTab));
        } catch (error) {
            console.error('Failed to fetch job list:', error);
        } finally {
            setLoading(false);
        }
    };

    const scrollToTop = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const handleDetailClick = (jobPostingCode: string) => {
        navigate(`/announcement/${jobPostingCode}`);
    };

    if (jobList.length === 0) {
        return <EmptyState message="등록한 일감이 없습니다."/>;
    }

    return (
        <div>
            <main>
                <button type="button" className="top-btn" onClick={scrollToTop}>
                    <i className="fa-solid fa-chevron-up"></i>
                    <span className="blind">맨위로</span>
                </button>
                <section>
                    <div className="inner">
                        {jobList.map((job) => (
                            <div key={job.jobPostingCode} className="round-box">
                                <div className="round-body">
                                    <div className="info-row">
                                        <span className="location-tit">{job.address}</span>
                                        <div className={`flag ${job.workType === 'HOURLY' ? '' : 'gray'}`}>
                                            {job.workType === 'HOURLY' ? '시간제' : '종일제'}
                                        </div>
                                    </div>
                                    <div className="info-row top-line">
                                        <span className="label">공고</span>
                                        <span className="cont">{job.serviceType}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">어르신 이름</span>
                                        <span className="cont">{job.elderName}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">시작일</span>
                                        <span className="cont">{job.careStartDate}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">간병기간</span>
                                        <span className="cont">{job.careDayCount}일</span>
                                    </div>
                                    <div className="info-row top-line">
                                        <span className="label">비용({job.workType === 'HOURLY' ? '시급' : '일급'})</span>
                                        <span className="cont">{getCostInfo(job).cost.toLocaleString()}원</span>
                                    </div>
                                    {getCostInfo(job).showOriginal && (
                                        <div className="info-row">
                                            <span className="label">최초 비용</span>
                                            <span className="cont">{getCostInfo(job).original.toLocaleString()}원</span>
                                        </div>
                                    )}
                                    <div className="info-row">
                                        <span className="label">비용협의</span>
                                        <span className="cont">{job.payNegotiable}</span>
                                    </div>
                                    <div className="info-row foot">
                                        <span className={`status ${getStatusDisplay(job).className}`}>
                                            {getStatusDisplay(job).label}
                                        </span>
                                        <a
                                            className="foot-line-btn"
                                            onClick={() => handleDetailClick(job.jobPostingCode)}
                                        >
                                            <i className="fa-solid fa-magnifying-glass"></i>공고 자세히 보기
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
}
